import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ConnectedHeader from '@hkm/components/shared/LayoutComponents/ConnectedHeader';
import MaintenanceStatusBadge from '@hkm/components/shared/MaintenanceStatusBadge/MaintenanceStatusBadge';
import RoomStatusBadge from '@hkm/components/shared/RoomStatusBadge/RoomStatusBadge';
import { getDiscrepancyColor } from '@hkm/shared/helpers/getDiscrapencyColor';
import {
  EnabledRoomStatesPurpose,
  useEnabledRoomStates
} from '@hkm/shared/hooks/useEnabledRoomStates';

import {
  DiscrepancyType,
  RoomMaintenanceStatus,
  RoomStatus
} from '@ac/library-api';
import { AcBadge } from '@ac/mobile-components/dist/components/badge';
import { AcBox } from '@ac/mobile-components/dist/components/box';
import { AcFlex } from '@ac/mobile-components/dist/components/flex';
import { AcBody } from '@ac/mobile-components/dist/components/layout';
import { AcText } from '@ac/mobile-components/dist/components/text';
import {
  AlignItems,
  Color,
  TextSize,
  TextWeight
} from '@ac/mobile-components/dist/enums';

import './HelpContainer.css';

const HelpContainer: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const roomStates = useEnabledRoomStates(EnabledRoomStatesPurpose.DISPLAY);

  const goBack = useCallback(() => {
    navigate(-1);
  }, []);

  return (
    <>
      <ConnectedHeader close={goBack}>{t('HELP.TITLE')}</ConnectedHeader>
      <AcBody className="help-body">
        <div className="help-section ac-spacing-bottom-md">
          <AcText
            color={Color.Black}
            size={TextSize.Main2}
            weight={TextWeight.Semibold}
            uppercase={true}
          >
            {t('HELP.DISCREPANCY.LABEL')}
          </AcText>
          <AcFlex
            alignItems={AlignItems.center}
            className="ac-spacing-top-md"
            wrapped={true}
          >
            <AcBox size={6}>
              <AcBadge
                label={t('GLOBAL.DISCREPANCY.Skip')}
                backgroundColor={getDiscrepancyColor(DiscrepancyType.Skip)}
              />
            </AcBox>
            <AcBox size={6}>
              <AcBadge
                label={t('GLOBAL.DISCREPANCY.Sleep')}
                backgroundColor={getDiscrepancyColor(DiscrepancyType.Sleep)}
              />
            </AcBox>
            <AcBox size={6} className="ac-spacing-top-md">
              <AcBadge
                label={t('GLOBAL.DISCREPANCY.Occupancy')}
                backgroundColor={getDiscrepancyColor(DiscrepancyType.Occupancy)}
              />
            </AcBox>
          </AcFlex>
        </div>

        <div className="help-section ac-spacing-top-md ac-spacing-bottom-md">
          <AcText
            color={Color.Black}
            size={TextSize.Main2}
            weight={TextWeight.Semibold}
          >
            {t('HELP.RESERVATION_STATUS.LABEL')}
          </AcText>
          <AcFlex alignItems={AlignItems.flexStart}>
            <AcBox size={6}>
              <div className="ac-spacing-top-md">
                <AcText>{t('HELP.RESERVATION_STATUS.RESERVED')}</AcText>
              </div>
              <div className="ac-spacing-top-md">
                <AcText>{t('HELP.RESERVATION_STATUS.DUE_IN')}</AcText>
              </div>
              <div className="ac-spacing-top-md">
                <AcText>{t('HELP.RESERVATION_STATUS.IN_HOUSE')}</AcText>
              </div>
              <div className="ac-spacing-top-md">
                <AcText>{t('HELP.RESERVATION_STATUS.DUE_OUT')}</AcText>
              </div>
            </AcBox>
            <AcBox size={6}>
              <div className="ac-spacing-top-md">
                <AcText>{t('HELP.RESERVATION_STATUS.CHECKED_OUT')}</AcText>
              </div>
              <div className="ac-spacing-top-md">
                <AcText>{t('HELP.RESERVATION_STATUS.CANCELLED')}</AcText>
              </div>
              <div className="ac-spacing-top-md">
                <AcText>{t('HELP.RESERVATION_STATUS.NO_SHOW')}</AcText>
              </div>
            </AcBox>
          </AcFlex>
        </div>

        <div className="help-section ac-spacing-top-md ac-spacing-bottom-md">
          <AcText
            color={Color.Black}
            size={TextSize.Main2}
            weight={TextWeight.Semibold}
            uppercase={true}
          >
            {t('HELP.ROOM_STATUS.LABEL')}
          </AcText>
          <AcFlex alignItems={AlignItems.center} wrapped={true}>
            {roomStates.map(state => (
              <AcBox key={state.code!} size={6}>
                <div className="ac-spacing-top-md">
                  <RoomStatusBadge
                    status={state.code! as RoomStatus}
                    showLabel={true}
                  />
                </div>
              </AcBox>
            ))}
          </AcFlex>
        </div>

        <div className="help-section ac-spacing-top-md ac-spacing-bottom-md">
          <AcText
            color={Color.Black}
            size={TextSize.Main2}
            weight={TextWeight.Semibold}
            uppercase={true}
          >
            {t('HELP.FRONT_DESK_STATUS.LABEL')}
          </AcText>
          <AcFlex alignItems={AlignItems.flexStart}>
            <AcBox size={6}>
              <div className="ac-spacing-top-md">
                <AcText>{t('HELP.FRONT_DESK_STATUS.VACANT')}</AcText>
              </div>
            </AcBox>
            <AcBox size={6}>
              <div className="ac-spacing-top-md">
                <AcText>{t('HELP.FRONT_DESK_STATUS.OCCUPIED')}</AcText>
              </div>
            </AcBox>
          </AcFlex>
        </div>

        <div className="help-section ac-spacing-top-md">
          <AcText
            color={Color.Black}
            size={TextSize.Main2}
            weight={TextWeight.Semibold}
            uppercase={true}
          >
            {t('HELP.OOO_OOS_STATUS.LABEL')}
          </AcText>
          <AcFlex alignItems={AlignItems.flexStart}>
            <AcBox size={6}>
              <div className="ac-spacing-top-md">
                <AcText>
                  <MaintenanceStatusBadge
                    status={RoomMaintenanceStatus.OutOfOrder}
                    showLabel={true}
                  />
                </AcText>
              </div>
            </AcBox>
            <AcBox size={6}>
              <div className="ac-spacing-top-md">
                <AcText>
                  <MaintenanceStatusBadge
                    status={RoomMaintenanceStatus.OutOfService}
                    showLabel={true}
                  />
                </AcText>
              </div>
            </AcBox>
          </AcFlex>
        </div>
      </AcBody>
    </>
  );
};

export default HelpContainer;



// WEBPACK FOOTER //
// ./src/components/Housekeeping/Help/HelpContainer.tsx